import { useState, useEffect } from "react";
import {
  Menu,
  X,
  ShoppingCart,
  Search,
  ChevronDown,
  ChevronUp,
  Filter,
  SlidersHorizontal,
  ArrowLeft,
  Heart,
  Send,
  AlertCircle,
} from "lucide-react";

import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { fetchUserLikes, toggleLike } from "../../redux/likesSlice";
import axios from "axios";

// Couleurs du drapeau du Niger (reprises de votre code)
const COLORS = {
  orange: "#E87E04",
  white: "#FFFFFF",
  green: "#008751",
  darkGreen: "#006B3F",
  lightOrange: "#FFE0B2",
  lightGreen: "#CCFFCC",
  gray: "#718096",
  lightGray: "#E2E8F0",
  paleGreen: "#E6FFEA",
};
const BackendUrl = process.env.REACT_APP_Backend_Url;
function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function AllProductsPage() {
  const DATA_Products = useSelector((state) => state.products.data);
  const DATA_Categories = useSelector((state) => state.products.categories);
  const navigate = useNavigate();
  // États pour les filtres et la recherche
  const [selectedCategory, setSelectedCategory] = useState(1); // "Tous" par défaut
  const [searchQuery, setSearchQuery] = useState("");
  const [priceRange, setPriceRange] = useState({ min: 0, max: 100000 }); // Augmenté pour correspondre aux prix en francs
  const [sortOption, setSortOption] = useState("newest");
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 9;
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationType, setNotificationType] = useState("success");
  // // Pour déboguer
  // useEffect(() => {
  //   console.log("Products data:", DATA_Products);
  //   console.log("Selected category:", selectedCategory);
  // }, [DATA_Products, selectedCategory]);

  // Fonction pour obtenir le prix effectif (promo si disponible, sinon prix normal)
  const getEffectivePrice = (product) => {
    if (
      product.prixPromo &&
      Number(product.prixPromo) > 0 &&
      Number(product.prixPromo) < Number(product.prix)
    ) {
      return Number(product.prixPromo);
    }
    return Number(product.prix);
  };

  // Calcul du pourcentage de réduction
  const calculateDiscount = (originalPrice, promoPrice) => {
    return Math.round(((originalPrice - promoPrice) / originalPrice) * 100);
  };

  // Filtrage des produits selon les critères - CORRIGÉ
  const filteredProducts = DATA_Products
    ? DATA_Products.filter((product) => {
        // Filtrage par catégorie
        const categoryMatch =
          selectedCategory === 1 ||
          String(product.ClefCategorie) === String(selectedCategory);

        // Filtrage par recherche
        const searchMatch =
          product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (product.description &&
            String(extractTextFromHTML(product.description))
              .toLowerCase()
              .includes(searchQuery.toLowerCase()));

        // Filtrage par prix - prend en compte le prix promo si existant
        const effectivePrice = getEffectivePrice(product);
        const priceMatch =
          !isNaN(effectivePrice) &&
          effectivePrice >= priceRange.min &&
          effectivePrice <= priceRange.max;

        return categoryMatch && searchMatch && priceMatch;
      })
    : [];

  // Tri des produits - mis à jour pour utiliser le prix effectif
  const sortedProducts = [...(filteredProducts || [])].sort((a, b) => {
    const priceA = getEffectivePrice(a);
    const priceB = getEffectivePrice(b);

    switch (sortOption) {
      case "price-asc":
        return priceA - priceB;
      case "price-desc":
        return priceB - priceA;
      case "name-asc":
        return a.name.localeCompare(b.name);
      case "name-desc":
        return b.name.localeCompare(a.name);
      case "newest":
      default:
        return (
          new Date(b.dateCreating || b.createdAt) -
          new Date(a.dateCreating || a.createdAt)
        );
    }
  });

  // Pagination
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = sortedProducts?.slice(
    indexOfFirstProduct,
    indexOfLastProduct
  );
  const totalPages = Math.ceil((sortedProducts?.length || 0) / productsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  // Réinitialiser les filtres
  const resetFilters = () => {
    setSelectedCategory(1);
    setSearchQuery("");
    setPriceRange({ min: 0, max: 100000 });
    setSortOption("newest");
    setCurrentPage(1);
  };

  function extractTextFromHTML(htmlString) {
    // Crée un élément temporaire pour parser le HTML
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlString;

    // Récupère uniquement le texte lisible
    return tempDiv.innerText.trim();
  }

  const dispatch = useDispatch();
  const likedProducts = useSelector((state) => state.likes.likedProducts);
  const userId = JSON.parse(localStorage.getItem("userEcomme"))?.id;

  useEffect(() => {
    if (userId) {
      dispatch(fetchUserLikes(userId));
    }
  }, [userId, dispatch]);

  const showToast = (message, type = "success") => {
    setNotificationMessage(message);
    setNotificationType(type);
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  const handleLikeClick = async (product, e) => {
    e.stopPropagation();

    if (!userId) {
      showToast("Veuillez vous connecter pour ajouter des favoris", "error");
      return;
    }

    try {
      await dispatch(toggleLike({ userId, product })).unwrap();
      showToast(
        likedProducts.includes(product._id)
          ? "Produit retiré des favoris"
          : "Produit ajouté aux favoris"
      );
    } catch (error) {
      showToast("Une erreur est survenue", "error");
      console.error("Erreur:", error);
    }
  };

  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterStatus, setNewsletterStatus] = useState({
    isSubmitting: false,
    success: "",
    error: "",
  });

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setNewsletterStatus({
      isSubmitting: true,
      success: "",
      error: "",
    });

    try {
      const normalizedEmail = newsletterEmail.trim().toLowerCase();
      if (!normalizedEmail) {
        throw new Error("Veuillez saisir votre adresse email");
      }

      await axios.post(`${BackendUrl}/api/newsletter/subscribe`, {
        email: normalizedEmail,
        source: "detail-homme",
      });

      setNewsletterStatus({
        isSubmitting: false,
        success:
          "Inscription enregistrée. Vérifiez votre e-mail pour confirmer.",
        error: "",
      });
      setNewsletterEmail("");
    } catch (error) {
      setNewsletterStatus({
        isSubmitting: false,
        success: "",
        error:
          error?.response?.data?.message ||
          error.message ||
          "Erreur lors de l'inscription à la newsletter.",
      });
    }
  };
  // ➕ Affichage temporaire
  useEffect(() => {
    if (newsletterStatus.success || newsletterStatus.error) {
      const timeout = setTimeout(() => {
        setNewsletterStatus({ success: null, error: null });
      }, 3000); // 3 secondes

      return () => clearTimeout(timeout); // Clean si ça change trop vite
    }
  }, [newsletterStatus]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {showNotification && (
        <div
          className={cn(
            "fixed top-4 right-4 z-50 px-4 py-3 rounded shadow-lg transition-all duration-300",
            notificationType === "success"
              ? "bg-green-100 border-green-400 text-green-700"
              : "bg-red-100 border-red-400 text-red-700"
          )}
        >
          <p className="text-sm">{notificationMessage}</p>
        </div>
      )}
      {/* Header Banner */}
      <div
        className="bg-gradient-to-r from-green-600 to-green-800 text-white py-8"
        style={{
          background: `linear-gradient(to right, ${COLORS.green}, ${COLORS.darkGreen})`,
        }}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center mb-4">
            <a
              onClick={() => navigate(`/`)}
              className="flex items-center text-white hover:opacity-80 transition-opacity"
            >
              <ArrowLeft size={20} className="mr-2" />
              <span>Retour à l'accueil</span>
            </a>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold">Tous nos produits</h1>
          <p className="mt-2 text-green-100">
            Découvrez notre collection complète d'artisanat nigérien
          </p>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Sidebar filters - desktop version */}
          <aside className="hidden md:block w-64 bg-white rounded-lg shadow-md p-4 h-fit sticky top-4">
            <h2 className="font-bold text-lg mb-4">Filtres</h2>

            {/* Search */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Recherche
              </label>
              <div className="relative">
                <input
                  type="text"
                  className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring focus:ring-green-200 focus:border-green-500 outline-none"
                  placeholder="Rechercher..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <Search
                  size={18}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
              </div>
            </div>

            {/* Categories */}
            <div className="mb-6">
              <h3 className="font-medium text-gray-800 mb-2">Catégories</h3>
              <div className="space-y-1">
                <div
                  className={`cursor-pointer px-2 py-1 rounded-md ${
                    selectedCategory === 1
                      ? "text-white"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                  style={{
                    backgroundColor: selectedCategory === 1 ? COLORS.green : "",
                  }}
                  onClick={() => setSelectedCategory(1)}
                >
                  Tous
                </div>
                {DATA_Categories?.map((category) => (
                  <div
                    key={category._id}
                    className={`cursor-pointer px-2 py-1 rounded-md ${
                      selectedCategory === category._id
                        ? "text-white"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                    style={{
                      backgroundColor:
                        selectedCategory === category._id ? COLORS.green : "",
                    }}
                    onClick={() => setSelectedCategory(category._id)}
                  >
                    {category.name}
                  </div>
                ))}
              </div>
            </div>

            {/* Price Range */}
            <div className="mb-6">
              <h3 className="font-medium text-gray-800 mb-2">Prix (€)</h3>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="0"
                  className="w-24 px-2 py-1 border rounded-md"
                  value={priceRange.min}
                  onChange={(e) =>
                    setPriceRange({
                      ...priceRange,
                      min: Number(e.target.value),
                    })
                  }
                />
                <span>à</span>
                <input
                  type="number"
                  min="0"
                  className="w-24 px-2 py-1 border rounded-md"
                  value={priceRange.max}
                  onChange={(e) =>
                    setPriceRange({
                      ...priceRange,
                      max: Number(e.target.value),
                    })
                  }
                />
              </div>
            </div>

            {/* Sort By */}
            <div className="mb-6">
              <h3 className="font-medium text-gray-800 mb-2">Trier par</h3>
              <select
                className="w-full px-3 py-2 border rounded-lg focus:ring focus:ring-green-200 focus:border-green-500 outline-none"
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
              >
                <option value="newest">Plus récents</option>
                <option value="price-asc">Prix croissant</option>
                <option value="price-desc">Prix décroissant</option>
                <option value="name-asc">Nom (A-Z)</option>
                <option value="name-desc">Nom (Z-A)</option>
              </select>
            </div>

            {/* Reset button */}
            <button
              className="w-full py-2 rounded-lg font-medium text-white"
              style={{ backgroundColor: COLORS.orange }}
              onClick={resetFilters}
            >
              Réinitialiser les filtres
            </button>
          </aside>

          {/* Mobile filters button and modal */}
          <div className="md:hidden mb-4">
            <button
              className="w-full flex items-center justify-center px-4 py-2 rounded-lg bg-white shadow border border-gray-200"
              onClick={() => setShowFilters(!showFilters)}
            >
              <SlidersHorizontal size={20} className="mr-2" />
              Filtres et tri
              {showFilters ? (
                <ChevronUp size={18} className="ml-2" />
              ) : (
                <ChevronDown size={18} className="ml-2" />
              )}
            </button>

            {showFilters && (
              <div className="mt-2 bg-white rounded-lg shadow-lg p-4 border border-gray-200">
                {/* Search */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Recherche
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring focus:ring-green-200 focus:border-green-500 outline-none"
                      placeholder="Rechercher..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <Search
                      size={18}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    />
                  </div>
                </div>

                {/* Categories */}
                <div className="mb-4">
                  <h3 className="font-medium text-gray-800 mb-2">Catégories</h3>
                  <div className="flex flex-wrap gap-2">
                    <button
                      className={`px-3 py-1 rounded-full text-sm font-medium ${
                        selectedCategory === 1
                          ? "text-white"
                          : "text-gray-700 bg-gray-100"
                      }`}
                      style={{
                        backgroundColor:
                          selectedCategory === 1 ? COLORS.green : "",
                      }}
                      onClick={() => setSelectedCategory(1)}
                    >
                      Tous
                    </button>
                    {DATA_Categories?.map((category) => (
                      <button
                        key={category._id}
                        className={`px-3 py-1 rounded-full text-sm font-medium ${
                          selectedCategory === category._id
                            ? "text-white"
                            : "text-gray-700 bg-gray-100"
                        }`}
                        style={{
                          backgroundColor:
                            selectedCategory === category._id
                              ? COLORS.green
                              : "",
                        }}
                        onClick={() => setSelectedCategory(category._id)}
                      >
                        {category.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Price Range */}
                <div className="mb-4">
                  <h3 className="font-medium text-gray-800 mb-2">Prix (€)</h3>
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      min="0"
                      className="w-24 px-2 py-1 border rounded-md"
                      value={priceRange.min}
                      onChange={(e) =>
                        setPriceRange({
                          ...priceRange,
                          min: Number(e.target.value),
                        })
                      }
                    />
                    <span>à</span>
                    <input
                      type="number"
                      min="0"
                      className="w-24 px-2 py-1 border rounded-md"
                      value={priceRange.max}
                      onChange={(e) =>
                        setPriceRange({
                          ...priceRange,
                          max: Number(e.target.value),
                        })
                      }
                    />
                  </div>
                </div>

                {/* Sort By */}
                <div className="mb-4">
                  <h3 className="font-medium text-gray-800 mb-2">Trier par</h3>
                  <select
                    className="w-full px-3 py-2 border rounded-lg"
                    value={sortOption}
                    onChange={(e) => setSortOption(e.target.value)}
                  >
                    <option value="newest">Plus récents</option>
                    <option value="price-asc">Prix croissant</option>
                    <option value="price-desc">Prix décroissant</option>
                    <option value="name-asc">Nom (A-Z)</option>
                    <option value="name-desc">Nom (Z-A)</option>
                  </select>
                </div>

                {/* Reset button */}
                <button
                  className="w-full py-2 rounded-lg font-medium text-white"
                  style={{ backgroundColor: COLORS.orange }}
                  onClick={resetFilters}
                >
                  Réinitialiser les filtres
                </button>
              </div>
            )}
          </div>

          {/* Products Grid */}
          <div className="flex-1">
            {/* Results summary */}
            <div className="flex justify-between items-center mb-6">
              <p className="text-gray-600">
                {sortedProducts?.length || 0} produits trouvés
              </p>
              <div className="hidden md:block">
                <label className="mr-2 text-gray-600">Trier par:</label>
                <select
                  className="px-3 py-1 border rounded-lg focus:ring focus:ring-green-200 focus:border-green-500 outline-none"
                  value={sortOption}
                  onChange={(e) => setSortOption(e.target.value)}
                >
                  <option value="newest">Plus récents</option>
                  <option value="price-asc">Prix croissant</option>
                  <option value="price-desc">Prix décroissant</option>
                  <option value="name-asc">Nom (A-Z)</option>
                  <option value="name-desc">Nom (Z-A)</option>
                </select>
              </div>
            </div>

            {/* No results message */}
            {currentProducts?.length === 0 && (
              <div className="bg-white rounded-lg shadow-md p-8 text-center">
                <div className="text-gray-400 mb-4">
                  <Search size={48} className="mx-auto" />
                </div>
                <h3 className="text-xl font-bold mb-2">Aucun produit trouvé</h3>
                <p className="text-gray-600 mb-4">
                  Essayez de modifier vos filtres ou votre recherche.
                </p>
                <button
                  className="px-4 py-2 rounded-lg font-medium text-white"
                  style={{ backgroundColor: COLORS.orange }}
                  onClick={resetFilters}
                >
                  Réinitialiser les filtres
                </button>
              </div>
            )}

            {/* Products grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentProducts?.map((product) => (
                <div
                  key={product._id}
                  className="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:scale-105"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      onClick={() => navigate(`/ProduitDétail/${product?._id}`)}
                      src={product?.image1 || product?.image}
                      alt={product?.name}
                      className="w-full h-full object-cover"
                    />
                    {/* Badge de promotion si applicable */}
                    {product.prixPromo &&
                      Number(product.prixPromo) > 0 &&
                      Number(product.prixPromo) < Number(product.prix) && (
                        <div
                          className="absolute top-2 left-2 px-2 py-1 rounded-full text-white text-xs font-bold"
                          style={{ backgroundColor: COLORS.orange }}
                        >
                          -
                          {calculateDiscount(
                            Number(product.prix),
                            Number(product.prixPromo)
                          )}
                          %
                        </div>
                      )}

                    <button
                      onClick={(e) => handleLikeClick(product, e)}
                      className={cn(
                        "absolute top-3 left-3 p-2 rounded-full shadow-lg transition-all duration-300 z-20",
                        likedProducts.includes(product._id)
                          ? "bg-red-50 hover:bg-red-100"
                          : "bg-white hover:bg-emerald-50"
                      )}
                    >
                      <Heart
                        className={cn(
                          "w-5 h-5 transition-colors duration-300",
                          likedProducts.includes(product._id)
                            ? "text-red-500 fill-red-500"
                            : "text-emerald-600"
                        )}
                      />
                    </button>
                  </div>
                  <div className="p-4">
                    <span
                      className="text-xs font-medium px-2 py-1 rounded-full text-white inline-block mb-2"
                      style={{ backgroundColor: COLORS.green }}
                    >
                      {DATA_Categories?.find(
                        (cat) => cat._id === product.ClefCategorie
                      )?.name || "Catégorie"}
                    </span>
                    <h3 className="font-bold text-lg mb-1">{product?.name}</h3>
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                      {/* Essaie d'extraire le texte brut de la description HTML si nécessaire */}
                      {extractTextFromHTML(product.description)}
                    </p>
                    <div className="flex justify-between items-center">
                      {/* Affichage du prix avec réduction si applicable */}
                      <div>
                        {product.prixPromo &&
                        Number(product.prixPromo) > 0 &&
                        Number(product.prixPromo) < Number(product.prix) ? (
                          <div>
                            <span
                              className="font-bold text-lg mr-2"
                              style={{ color: COLORS.orange }}
                            >
                              {Number(product.prixPromo).toLocaleString()} €
                            </span>
                            <span className="text-gray-500 text-sm line-through">
                              {Number(product.prix).toLocaleString()} €
                            </span>
                          </div>
                        ) : (
                          <span
                            className="font-bold text-lg"
                            style={{ color: COLORS.orange }}
                          >
                            {Number(product.prix).toLocaleString()} €
                          </span>
                        )}
                      </div>
                      <button
                        className="px-3 py-1 rounded-full text-sm text-white font-medium flex items-center"
                        style={{ backgroundColor: COLORS.green }}
                        onClick={() =>
                          navigate(`/ProduitDétail/${product?._id}`)
                        }
                      >
                        <ShoppingCart size={16} className="mr-1" />
                        Ajouter
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 flex justify-center">
                <div className="flex space-x-1">
                  <button
                    onClick={() => paginate(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className={`px-3 py-1 rounded-md ${
                      currentPage === 1
                        ? "bg-gray-200 text-gray-500"
                        : "bg-white text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    Précédent
                  </button>

                  {Array.from({ length: totalPages }, (_, index) => {
                    const pageNumber = index + 1;
                    // Display only current page, first, last, and nearby pages
                    if (
                      pageNumber === 1 ||
                      pageNumber === totalPages ||
                      (pageNumber >= currentPage - 1 &&
                        pageNumber <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => paginate(pageNumber)}
                          className={`w-8 h-8 flex items-center justify-center rounded-md ${
                            currentPage === pageNumber
                              ? "text-white font-medium"
                              : "bg-white text-gray-700 hover:bg-gray-100"
                          }`}
                          style={{
                            backgroundColor:
                              currentPage === pageNumber ? COLORS.green : "",
                          }}
                        >
                          {pageNumber}
                        </button>
                      );
                    } else if (
                      (pageNumber === currentPage - 2 && currentPage > 3) ||
                      (pageNumber === currentPage + 2 &&
                        currentPage < totalPages - 2)
                    ) {
                      return (
                        <span
                          key={pageNumber}
                          className="w-8 h-8 flex items-center justify-center"
                        >
                          ...
                        </span>
                      );
                    }
                    return null;
                  })}

                  <button
                    onClick={() =>
                      paginate(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1 rounded-md ${
                      currentPage === totalPages
                        ? "bg-gray-200 text-gray-500"
                        : "bg-white text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    Suivant
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Feature Banner */}
      {/* Messages d'état pour la newsletter */}
      {newsletterStatus.success && (
        <div className="mb-6 p-4 rounded-lg bg-green-50 border border-green-200 text-green-700">
          {newsletterStatus.success}
        </div>
      )}

      {newsletterStatus.error && (
        <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 flex items-start">
          <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
          <span>{newsletterStatus.error}</span>
        </div>
      )}
      <section className="bg-white py-8 border-t border-gray-200">
        <div className="container mx-auto px-4">
          <div
            className="rounded-xl overflow-hidden shadow-lg p-6"
            style={{ backgroundColor: COLORS.lightGreen }}
          >
            <div className="flex flex-col md:flex-row items-center">
              <div className="md:w-1/4 mb-6 md:mb-0 flex justify-center">
                <div
                  className="w-24 h-24 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: COLORS.green }}
                >
                  <ShoppingCart size={40} className="text-white" />
                </div>
              </div>

              <div className="md:w-3/4 text-center md:text-left">
                <h2 className="text-2xl font-bold mb-2">
                  Restez informé de nos nouveautés
                </h2>
                <p className="text-gray-700 mb-4">
                  Inscrivez-vous à notre newsletter pour recevoir nos offres
                  exclusives et découvrir les nouveaux produits du Niger.
                </p>
                <form onSubmit={handleNewsletterSubmit}>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="email"
                      value={newsletterEmail}
                      onChange={(e) => setNewsletterEmail(e.target.value)}
                      placeholder="Votre adresse email"
                      className="px-4 py-2 rounded-lg border flex-grow focus:ring focus:ring-green-200 focus:border-green-500 outline-none"
                    />
                    <button
                      className="px-6 py-2 rounded-lg font-medium text-white"
                      style={{ backgroundColor: COLORS.orange }}
                      type="submit"
                    >
                      {newsletterStatus.isSubmitting ? (
                        <span className="flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Traitement...
                        </span>
                      ) : (
                        <span className="flex items-center">
                          <Send className="w-4 h-4 mr-2" />
                          S'inscrire
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
